'use client';

import { useState } from 'react';

import { FlatQuotationLine } from '@ravago/shared/page-data/models/elements';
import { QuotationStatus } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useShoppingCartContext } from '../../../../context/shopping-cart-context';
import { QuoteProductPriceItem } from '../models/quote-product-price-item.model';

import { QuoteProductList } from './quote-product-list';
import { QuoteProductPriceDetails } from './quote-product-price-details';
import { QuoteProductPriceSelectorMobile } from './quote-product-price-selector-mobile';

interface Props {
   quoteProductPriceItems: QuoteProductPriceItem[];
   productDetailBaseRoute: string;
   documentsAnchor: string;
   channelName: string;
   quoteStatus: QuotationStatus;
   validUntil: string;
   showShoppingCartBtn: boolean;
   hasOrderingPermission: boolean;
   canViewPrices: boolean;
   bracketPricingEnabled?: boolean;
   leadTimeEnabled?: boolean;
   deliveryAddressId?: number;
}

export const QuoteProductPriceSelector = ({
   quoteProductPriceItems,
   productDetailBaseRoute,
   documentsAnchor,
   channelName,
   quoteStatus,
   validUntil,
   showShoppingCartBtn,
   hasOrderingPermission,
   canViewPrices,
   bracketPricingEnabled = false,
   leadTimeEnabled = false,
   deliveryAddressId
}: Props) => {
   const translate = useTranslations();
   const cartContext = useShoppingCartContext();
   const { phone } = useBreakpoint();

   const [selectedProductId, setSelectedProductId] = useState(quoteProductPriceItems[0]?.id ?? '');

   const selectedProduct = quoteProductPriceItems.find((product) => product.id === selectedProductId) ?? quoteProductPriceItems[0];

   const isOrderingBlocked = cartContext.isAddingToCart || !hasOrderingPermission || quoteStatus === 'EXPIRED';

   const handleAddToCart = (line: FlatQuotationLine, quantity: number) => {
      cartContext.addToCart({ type: 'QUOTATION', id: line.lineId }, line.lineOrderableProduct, line.linePackaging, {
         amount: quantity,
         unitOfMeasure: line.lineQuantityUOM
      });
   };

   if (!selectedProduct) {
      return (
         <Paper>
            <Typography variant="small" color="subtle">
               {translate('prices.no-quote')}
            </Typography>
         </Paper>
      );
   }

   return (
      <>
         <BreakpointSwitch excludeViews={['phone']}>
            <Grid>
               <GridCol spanDesktop={6} spanTablet={12} spanPhone={12}>
                  <QuoteProductList
                     products={quoteProductPriceItems}
                     selectedProductId={selectedProduct.id}
                     onProductChange={setSelectedProductId}
                     leadTimeEnabled={leadTimeEnabled}
                     deliveryAddressId={deliveryAddressId}
                  />
               </GridCol>
               <GridCol spanDesktop={6} spanTablet={12} spanPhone={12}>
                  <QuoteProductPriceDetails
                     key={selectedProduct.id}
                     product={selectedProduct}
                     productDetailBaseRoute={productDetailBaseRoute}
                     documentsAnchor={documentsAnchor}
                     channelName={channelName}
                     validUntil={validUntil}
                     canViewPrices={canViewPrices}
                     showShoppingCartBtn={showShoppingCartBtn}
                     hasOrderingPermission={hasOrderingPermission}
                     isOrderingBlocked={isOrderingBlocked}
                     bracketPricingEnabled={bracketPricingEnabled}
                     leadTimeEnabled={leadTimeEnabled}
                     deliveryAddressId={deliveryAddressId}
                     onAddToCart={handleAddToCart}
                  />
               </GridCol>
            </Grid>
         </BreakpointSwitch>

         {phone && (
            <QuoteProductPriceSelectorMobile
               quoteProductPriceItems={quoteProductPriceItems}
               productDetailBaseRoute={productDetailBaseRoute}
               documentsAnchor={documentsAnchor}
               channelName={channelName}
               validUntil={validUntil}
               canViewPrices={canViewPrices}
               showShoppingCartBtn={showShoppingCartBtn}
               hasOrderingPermission={hasOrderingPermission}
               isOrderingBlocked={isOrderingBlocked}
               bracketPricingEnabled={bracketPricingEnabled}
               leadTimeEnabled={leadTimeEnabled}
               deliveryAddressId={deliveryAddressId}
               onAddToCart={handleAddToCart}
            />
         )}
      </>
   );
};
